/* ===========================================================
 * DockablePanel
 * -----------------------------------------------------------
 * Generischer Panel-Wrapper mit drei Zuständen:
 *  - "docked"     → normales Inline-Panel mit Header + Body
 *  - "collapsed"  → nur Header sichtbar (Klick re-expandiert)
 *  - "floating"   → ausgekoppeltes, frei drag- & resizebares Fenster (Portal)
 *  - "hidden"     → komplett ausgeblendet, Wiederherstellen über Side-Tab
 *
 * Nutzbar für jedes Composer-Sub-Panel (Library, Inspector, Timeline …).
 * =========================================================== */

import {
  type CSSProperties,
  type ReactNode,
  type PointerEvent as RPointerEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import type { FloatRect, PanelMode } from "./useDockLayout";

type DragKind =
  | { kind: "move"; startX: number; startY: number; rectX: number; rectY: number }
  | { kind: "resize"; edge: "se" | "s" | "e" | "w" | "sw"; startX: number; startY: number; rect: FloatRect };

type Props = {
  id: string;
  title: ReactNode;
  icon?: ReactNode;
  badge?: ReactNode;
  mode: PanelMode;
  /** Geometrie nur relevant in "floating". */
  float: FloatRect;
  /** Aktion beim Klick auf den Header (außerhalb der Buttons): kollabieren ↔ expandieren. */
  onToggleCollapse: () => void;
  onTogglePopup: () => void;
  onHide?: () => void;
  /** Wird in "floating" beim Drag/Resize aufgerufen. */
  onFloatChange: (rect: Partial<FloatRect>) => void;
  /** Wird im "docked"-Modus für den Body verwendet (z. B. min-height). */
  bodyStyle?: CSSProperties;
  /** Tooltip-Text für die Popup-Aktion. */
  popupTitle?: string;
  collapseTitle?: string;
  hideTitle?: string;
  className?: string;
  children: ReactNode;
};

export const DockablePanel = memo(function DockablePanel({
  id,
  title,
  icon,
  badge,
  mode,
  float,
  onToggleCollapse,
  onTogglePopup,
  onHide,
  onFloatChange,
  bodyStyle,
  popupTitle = "Als Popup ausklappen",
  collapseTitle = "Minimieren",
  hideTitle = "Ausblenden",
  className,
  children,
}: Props): JSX.Element {
  const dragRef = useRef<DragKind | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  /* === Drag (move + resize) für Float-Mode === */
  const handlePointerMove = useCallback(
    (ev: PointerEvent): void => {
      const d = dragRef.current;
      if (!d) return;
      if (d.kind === "move") {
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        onFloatChange({ x: d.rectX + dx, y: d.rectY + dy });
      } else {
        const dx = ev.clientX - d.startX;
        const dy = ev.clientY - d.startY;
        const r = d.rect;
        const next: Partial<FloatRect> = {};
        if (d.edge === "se" || d.edge === "s" || d.edge === "sw") next.h = r.h + dy;
        if (d.edge === "se" || d.edge === "e") next.w = r.w + dx;
        if (d.edge === "w" || d.edge === "sw") {
          next.w = r.w - dx;
          next.x = r.x + dx;
        }
        onFloatChange(next);
      }
    },
    [onFloatChange],
  );

  const stopDrag = useCallback((): void => {
    dragRef.current = null;
    setIsDragging(false);
    window.removeEventListener("pointermove", handlePointerMove);
    window.removeEventListener("pointerup", stopDrag);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
  }, [handlePointerMove]);

  useEffect(() => {
    return () => stopDrag();
  }, [stopDrag]);

  const beginMove = (ev: RPointerEvent<HTMLElement>): void => {
    if (mode !== "floating") return;
    if ((ev.target as HTMLElement).closest(".dock-btn,.dock-resize")) return;
    ev.preventDefault();
    dragRef.current = {
      kind: "move",
      startX: ev.clientX,
      startY: ev.clientY,
      rectX: float.x,
      rectY: float.y,
    };
    setIsDragging(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "grabbing";
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDrag);
  };

  const beginResize = (edge: "se" | "s" | "e" | "w" | "sw") => (ev: RPointerEvent<HTMLDivElement>): void => {
    ev.preventDefault();
    ev.stopPropagation();
    dragRef.current = {
      kind: "resize",
      edge,
      startX: ev.clientX,
      startY: ev.clientY,
      rect: { ...float },
    };
    setIsDragging(true);
    document.body.style.userSelect = "none";
    document.body.style.cursor = `${edge}-resize`;
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", stopDrag);
  };

  /* === Header — wird in allen Modi gerendert === */
  const header = useMemo(
    () => (
      <header
        className={`dock-header ${mode === "floating" ? "floating" : ""}`}
        onPointerDown={beginMove}
        onDoubleClick={(e) => {
          if ((e.target as HTMLElement).closest(".dock-btn")) return;
          onToggleCollapse();
        }}
        title={mode === "collapsed" ? "Klick zum Ausklappen" : undefined}
        role="button"
        aria-expanded={mode !== "collapsed"}
      >
        <span className="dock-title">
          {icon && <span className="dock-title-icon" aria-hidden>{icon}</span>}
          <span className="dock-title-text">{title}</span>
          {badge && <span className="dock-title-badge">{badge}</span>}
        </span>
        <span className="dock-actions">
          <button
            type="button"
            className="dock-btn"
            onClick={(e) => { e.stopPropagation(); onToggleCollapse(); }}
            title={mode === "collapsed" ? "Ausklappen" : collapseTitle}
            aria-label={mode === "collapsed" ? "Ausklappen" : collapseTitle}
          >
            {mode === "collapsed" ? "▢" : "—"}
          </button>
          <button
            type="button"
            className="dock-btn"
            onClick={(e) => { e.stopPropagation(); onTogglePopup(); }}
            title={mode === "floating" ? "Wieder andocken" : popupTitle}
            aria-label={mode === "floating" ? "Wieder andocken" : popupTitle}
          >
            {mode === "floating" ? "⤡" : "⤢"}
          </button>
          {onHide && (
            <button
              type="button"
              className="dock-btn dock-btn-close"
              onClick={(e) => { e.stopPropagation(); onHide(); }}
              title={hideTitle}
              aria-label={hideTitle}
            >
              ✕
            </button>
          )}
        </span>
      </header>
    ),
    [mode, icon, title, badge, collapseTitle, popupTitle, hideTitle, onToggleCollapse, onTogglePopup, onHide, beginMove],
  );

  /* === Float-Mode → Portal === */
  if (mode === "floating") {
    const style: CSSProperties = {
      left: float.x,
      top: float.y,
      width: float.w,
      height: float.h,
    };
    return createPortal(
      <div
        className={`dock-floating ${isDragging ? "is-dragging" : ""} dock-of-${id}`}
        style={style}
        data-panel-id={id}
        role="dialog"
        aria-label={typeof title === "string" ? title : id}
      >
        {header}
        <div className="dock-body" style={bodyStyle}>{children}</div>
        {/* Resize-Handles */}
        <div className="dock-resize dock-resize-e" onPointerDown={beginResize("e")} aria-hidden />
        <div className="dock-resize dock-resize-w" onPointerDown={beginResize("w")} aria-hidden />
        <div className="dock-resize dock-resize-s" onPointerDown={beginResize("s")} aria-hidden />
        <div className="dock-resize dock-resize-se" onPointerDown={beginResize("se")} aria-hidden />
        <div className="dock-resize dock-resize-sw" onPointerDown={beginResize("sw")} aria-hidden />
      </div>,
      document.body,
    );
  }

  /* === Hidden-Mode === */
  if (mode === "hidden") {
    return <></>;
  }

  /* === Docked / Collapsed === */
  return (
    <div className={`dock-panel mode-${mode} ${className ?? ""}`} data-panel-id={id}>
      {header}
      {mode !== "collapsed" && (
        <div className="dock-body" style={bodyStyle}>{children}</div>
      )}
    </div>
  );
});
